/**
 * Guide engine v3 - step helpers (#1024, epic #1020). Spec R1 / R4.
 *
 * Pure functions that read a step's contract fields. No DOM, no React, no state -
 * the headless runtime (GuideRuntimeContext) composes these with the DOM probes.
 * Kept pure so branching / button-ordering logic is unit-testable in isolation.
 */

/** A step's highlight ring selectors, in array order (empty when concept step). */
export const highlightSelectors = (step) => (step.highlight || []).map((h) => h.element);

/** The highlight object with the given step-local id, or null. */
export const highlightById = (step, id) => (step.highlight || []).find((h) => h.id === id) || null;

/**
 * The CSS selector a `click` advance fires on: the element of the highlight it
 * references (R4 - you click what is ringed). Null if the id has no highlight.
 * @param {object} step
 * @param {string} id  highlight id from the click condition
 * @returns {string|null}
 */
export const clickSelector = (step, id) => {
  const h = highlightById(step, id);
  return h ? h.element : null;
};

/** Advance conditions that the runtime polls for (appears / value), in array order. */
export const watchConditions = (step) =>
  (step.advance || []).filter((a) => a.on === 'appears' || a.on === 'value');

/** The manual conditions (button-bearing) of a step, in array order. */
export const manualConditions = (step) => (step.advance || []).filter((a) => a.on === 'manual');

/**
 * Manual buttons for the overlay to render (R4 button layout). Array order is
 * preserved; the LAST manual condition is the primary (the author sequences the
 * forward / primary button last so it renders bottom-right). The runtime exposes
 * `text` verbatim (or null); the overlay resolves the default label + position.
 * @param {object} step
 * @returns {{condition: object, text: (string|null), next: string, isPrimary: boolean}[]}
 */
export const manualButtons = (step) => {
  const conds = manualConditions(step);
  return conds.map((condition, i) => ({
    condition,
    text: condition.text || null,
    next: condition.next,
    isPrimary: i === conds.length - 1,
  }));
};
